import {FC, ReactElement, createElement, useState, useEffect, useMemo, useCallback} from 'react';
import './index-view-results.scss';
import {
  Box,
  Paper,
  SxProps,
  Table,
  TableBody,
  tableBodyClasses,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
} from '@mui/material';
import {IndexScreenViewType} from '../index-view-screen/index-view-screen';
import {PaginatedTableData, PaginatedTableItem} from '../../interfaces/paginated-table-data';
import {AffiliatedUser} from '../../backend/responses/affiliated-user';
import {translations} from '../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {Pallete} from '../../theme/bluebird';
import {SortIcon} from '../task-table/sort-icon';
import {isFirefox} from '../../utils/functions/user-agent';
import {LoadingContent} from '../loading-content/loading-content';
import {UserType} from '../../backend/enums/user-type';

interface IndexViewResultsProps {
  firstLoad: boolean;
  isLoading: boolean;
  data: PaginatedTableData<any>;
  setData: (data: PaginatedTableData<any>) => void;
  activeViewType: IndexScreenViewType;
  handleApplySort?: (sortString: string) => void;
  renderItem: {
    list?: {
      element: ({
        data,
        toLink,
        columns,
      }: {
        data: AffiliatedUser;
        toLink: string;
        columns?: unknown;
      }) => ReactElement;
      skeleton: () => ReactElement;
    };
    grid?: {
      element: ({
        data,
        toLink,
        columns,
      }: {
        data: AffiliatedUser;
        toLink: string;
        columns?: unknown;
      }) => ReactElement;
      skeleton: () => ReactElement;
      gridItemWidth: number;
    };
  };
  enabled?: boolean;
  currentPage?: number;
  currentSearch?: string;
  currentFilters?: string;
  currentSort?: string;
  emptyStateMessage: {title: string; subtitle?: string};
  noSearchResultsMessage?: {title: string; subtitle?: string};
  noFilteredResultsMessage?: {title: string; subtitle?: string};
  canWrite?: boolean;
  canDelete?: boolean;
  viewType?: UserType;
}

export const IndexViewResults = ({
  firstLoad,
  enabled,
  isLoading,
  data,
  setData,
  handleApplySort,
  activeViewType,
  renderItem,
  currentSearch,
  currentSort,
  currentFilters,
  currentPage,
  emptyStateMessage,
  noSearchResultsMessage,
  noFilteredResultsMessage,
  canWrite,
  canDelete,
  viewType = UserType.Senior,
}: IndexViewResultsProps) => {
  const {t} = useTranslation();

  const [users, setUsers] = useState(data);

  const [order, setOrder] = useState<'asc' | 'desc' | undefined>('asc');
  const [orderBy, setOrderBy] = useState<ColumnsId>();

  if (data.length !== users.length) {
    setUsers(data);
  }

  let sxOptions: SxProps<Theme> = {};

  if (activeViewType === 'grid' && renderItem.grid?.gridItemWidth) {
    sxOptions = {
      gridTemplateColumns: `repeat(auto-fill, ${renderItem.grid.gridItemWidth}px)`,
    };
  }

  const seniorMarkedAsInactive = (senior: AffiliatedUser) => {
    const index = data.findIndex((s) => s.item.user.id === senior.user.id);
    if (index === -1) {
      return;
    }
    const newSeniors = [...data];
    newSeniors[index].item.user.isActive = false;
    setData(newSeniors);
  };

  const userDeleted = (user: AffiliatedUser) => {
    const index = data.findIndex((s) => s.item.user.id === user.user.id);
    if (index === -1) {
      return;
    }
    const newUsers = [...data];
    newUsers.splice(index, 1);
    setData(newUsers);
  };

  const handleSort = (columnId: ColumnsId) => {
    const asc = orderBy === columnId && order === 'asc';
    setOrder(asc ? 'desc' : 'asc');
    setOrderBy(columnId);
  };

  const IndexUsers = () => (
    <>
      {data.map((data: PaginatedTableItem<AffiliatedUser>, index) =>
        createElement<{
          data: AffiliatedUser;
          columns?: unknown;
          toLink?: string;
          width?: string;
          key: number;
          canWrite?: boolean;
          canDelete?: boolean;
          onDelete: (user: AffiliatedUser) => void;
        }>(renderItem[activeViewType]?.element as FC, {
          data: data.item,
          columns: data.columns,
          toLink: data.toLink,
          key: index,
          canWrite: canWrite,
          canDelete: canDelete,
          onDelete: data.item.user.type === 'senior' ? seniorMarkedAsInactive : userDeleted,
        })
      )}
    </>
  );

  const tableContainerStyle: SxProps = {
    position: 'absolute',
    overflow: 'auto',

    width: isFirefox ? '-moz-available' : '-webkit-fill-available',
    maxHeight: isFirefox ? '70%' : '-webkit-fill-available',

    // borders
    marginBottom: '20px',
    marginRight: '20px',
    borderRadius: '5px',

    // scrollbar
    scrollbarWidth: 'thin',
    scrollbarHeight: 'thin',
    '&::-webkit-scrollbar': {
      width: '0.4em',
      height: '0.4em',
    },
  };

  const tableStyle: SxProps = {
    marginBottom: '29px',
    paddingX: '22px',
    width: '100%',

    // body dividers
    [`& .${tableBodyClasses.root}`]: {
      [`& .${tableCellClasses.root}`]: {
        borderBottom: 'none',
      },
    },
    // first divider
    [`& .${tableCellClasses.head}`]: {
      borderBottom: 'none',
    },

    borderCollapse: 'separate',
    borderSpacing: '0 10px',
  };

  const headerRowStyle = {
    '& .MuiTableCell-root': {
      backgroundColor: Pallete.white.base,
      color: Pallete.gray.base,
      fontSize: '18px',
      fontWeight: '500',
      lineHeight: '18px',
      paddingTop: '29px',
    },
  };

  type ColumnsId = 'name' | 'socialResponse' | 'birthday' | 'room' | 'status' | 'actions';

  const getSortStateFromCurrentSort = useCallback(
    (sort?: string): {orderBy?: ColumnsId; order?: 'asc' | 'desc'} => {
      const [columnId, direction] = sort?.trim().split(/\s+/, 2) ?? [];

      switch (columnId) {
        case 'name':
        case 'socialResponse':
        case 'birthday':
        case 'room':
        case 'status':
        case 'actions':
          return {
            orderBy: columnId,
            order: direction === 'DESC' ? 'desc' : 'asc',
          };
        default:
          return {};
      }
    },
    []
  );

  const tableColumns: Partial<
    Record<
      ColumnsId,
      {
        label: string;
        sortable: boolean;
        align?: 'right' | 'center';
        sx?: SxProps<Theme>;
      }
    >
  > = {
    name: {
      label: t(translations.office.professional_caregiver.seniors.table_view_screen.name),
      sortable: true,
      sx: {
        width: '25%',
      },
    },
    socialResponse: {
      label:
        viewType === UserType.Caregiver
          ? t(translations.office.professional_caregiver.seniors.table_view_screen.role)
          : t(translations.office.professional_caregiver.seniors.table_view_screen.social_response),
      sortable: false,
      sx: {
        width: '16.25%',
      },
    },
    birthday: {
      label:
        viewType === UserType.Caregiver
          ? t(translations.office.professional_caregiver.seniors.table_view_screen.email)
          : t(translations.office.professional_caregiver.seniors.table_view_screen.age),
      sortable: true,
      sx: {
        width: '16.25%',
      },
    },
    room: {
      label:
        viewType === UserType.Caregiver
          ? t(translations.office.professional_caregiver.seniors.table_view_screen.phone)
          : t(translations.office.professional_caregiver.seniors.table_view_screen.room),
      sortable: true,
      sx: {
        width: '16.25%',
      },
    },
    status: {
      label: t(translations.office.professional_caregiver.seniors.table_view_screen.status),
      sortable: false,
      align: 'center',
      sx: {
        width: '16.25%',
      },
    },
  };

  if (canWrite || canDelete) {
    tableColumns.actions = {
      label: '',
      sortable: false,
      sx: {
        width: '10%',
      },
    };
  }

  const displaySkeletons = useMemo(() => {
    return isLoading && !firstLoad;
  }, [firstLoad, isLoading]);

  const filtersHaveChanged = useMemo(() => {
    return isLoading && enabled && (currentFilters || currentSort) && currentPage === 1;
  }, [currentFilters, currentPage, currentSort, enabled, isLoading]);

  const filteredResultsNoData = useMemo(() => {
    return (
      !isLoading &&
      enabled &&
      (currentFilters || currentSort) &&
      currentPage === 1 &&
      data.length === 0
    );
  }, [currentFilters, currentPage, currentSort, data.length, enabled, isLoading]);

  const noSearchResultsData = useMemo(() => {
    return !isLoading && enabled && data.length === 0 && currentSearch && currentSearch.length > 0;
  }, [currentSearch, data.length, enabled, isLoading]);

  const noData = useMemo(() => {
    return !isLoading && enabled && data.length === 0;
  }, [data.length, isLoading, enabled]);

  useEffect(() => {
    const sortState = getSortStateFromCurrentSort(currentSort);

    if (!sortState.orderBy || !sortState.order) {
      return;
    }

    setOrderBy(sortState.orderBy);
    setOrder(sortState.order);
  }, [currentSort, getSortStateFromCurrentSort]);

  useEffect(() => {
    orderBy && order && handleApplySort && handleApplySort(`${orderBy} ${order?.toUpperCase()}`);
  }, [handleApplySort, order, orderBy]);

  if (displaySkeletons) {
    if (activeViewType === 'grid') {
      return (
        <Box className="index-view-results" sx={sxOptions}>
          {new Array(20).fill(1).map((_props, index) =>
            createElement(renderItem[activeViewType]?.skeleton as FC, {
              key: `skeleton_grid_${activeViewType}_${index}`,
            })
          )}
        </Box>
      );
    } else {
      return (
        <TableContainer component={Paper} sx={tableContainerStyle}>
          <Table stickyHeader sx={tableStyle}>
            <TableHead>
              <TableRow sx={headerRowStyle}>
                {Object.entries(tableColumns).map(([key, {label, sortable, sx, align}], index) => {
                  return (
                    <TableCell align={align} key={`cell_for_${key}_${index}`} sx={sx}>
                      {sortable ? (
                        <TableSortLabel
                          onClick={() => handleSort(key as ColumnsId)}
                          IconComponent={() => <SortIcon active={false} />}
                        >
                          {label}
                        </TableSortLabel>
                      ) : (
                        label
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {new Array(6).fill(1).map((_props, index) => (
                <TableRow key={`skeleton_row_${index}`}>
                  <TableCell colSpan={6} sx={{padding: '0 15px'}}>
                    {createElement(renderItem.list?.skeleton as FC)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }
  }

  if (filtersHaveChanged) {
    return (
      <div className="index-view-results-no-results">
        <div className="wrapper">
          <LoadingContent />
        </div>
      </div>
    );
  }

  if (filteredResultsNoData && noFilteredResultsMessage) {
    return (
      <div className="index-view-results-no-results">
        <div className="wrapper">
          <p className="title">{noFilteredResultsMessage.title}</p>
          <p className="subtitle">{noFilteredResultsMessage.subtitle}</p>
        </div>
      </div>
    );
  }

  if (noSearchResultsData && noSearchResultsMessage) {
    return (
      <div className="index-view-results-no-results">
        <div className="wrapper">
          <p className="title">{noSearchResultsMessage.title}</p>
          <p className="subtitle">{noSearchResultsMessage.subtitle}</p>
        </div>
      </div>
    );
  }

  if (noData) {
    return (
      <div className="index-view-results-no-results">
        <div className="wrapper">
          <p className="title">{emptyStateMessage.title}</p>
          <p className="subtitle">{emptyStateMessage.subtitle}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {activeViewType === 'grid' ? (
        <Box className="index-view-results" sx={sxOptions}>
          <IndexUsers />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={tableContainerStyle}>
          <Table stickyHeader sx={tableStyle}>
            <TableHead>
              <TableRow sx={headerRowStyle}>
                {Object.entries(tableColumns).map(([key, {label, sortable, sx, align}], index) => {
                  const columnId = key as ColumnsId;

                  const active = orderBy === columnId;

                  const sortDirection = active ? order : 'asc';

                  return (
                    <TableCell
                      align={align}
                      key={`cell_for_${columnId}_${index}`}
                      sortDirection={active ? order : undefined}
                      sx={sx}
                    >
                      {sortable ? (
                        <TableSortLabel
                          active={active}
                          direction={sortDirection}
                          onClick={() => handleSort(columnId)}
                          IconComponent={() => (
                            <SortIcon active={active} direction={sortDirection} />
                          )}
                          sx={{
                            '&.Mui-active': {
                              color: 'var(--dcm-gray)',
                            },
                          }}
                        >
                          {label}
                        </TableSortLabel>
                      ) : (
                        label
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              <IndexUsers />
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};
